import { AppSidebar } from '@neo/blocks/sidebar/app-sidebar'
import { useCallback } from 'react'
import { useNavigate } from 'router-v6'
import { getDefaultRoute } from 'v2/configs/routes'
import useFavouritesAndViews from 'v2/pages/conversations/hooks/use-get-favourites-views'
import { useFavoriteViewMutation } from 'v2/pages/conversations/mutations/conversations'
import {
  useActiveConversationsQuery,
  useInvitedContactsQuery,
  useViewsQuery,
} from 'v2/pages/conversations/queries'
import { useAllTasksQuery, useUnassignedTasksQuery } from 'v2/pages/conversations/tasks/queries'
import { useReportSections } from 'v2/pages/reports/hooks/use-report-sections'
import { useCurrentStatusQuery } from 'v2/pages/settings/org-status/queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { useRoutes } from 'v2/shared/hooks/use-routes'
import { useWorkspaceSidebarEnabled } from 'v2/shared/hooks/use-workspace-sidebar-enabled'
import { useAppStore } from 'v2/state/app'
import { TaskViewName } from 'v2/types'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { getLocalAuth } from 'v2/utils'
import { useOrgSwitch } from './create-organization-modal/use-org-switch'
import { StatusMenu } from './status-menu'
import { WorkspaceSidebar } from './workspace-sidebar/workspace-sidebar'

interface AppSidebarWrapperProps
  extends Omit<
    React.ComponentProps<typeof AppSidebar>,
    'avatarProps' | 'viewsData' | 'handleSwitchOrganization'
  > {}

export const AppSidebarWrapper: React.FC<AppSidebarWrapperProps> = (props) => {
  const navigate = useNavigate()
  const { data: profileData } = useProfileQuery()
  const { data: agentStatusData } = useCurrentStatusQuery()
  const { data: views } = useViewsQuery()
  const { favourites, views: publicViews } = useFavouritesAndViews()
  const routes = useRoutes() // Get filtered routes based on user permissions
  const showDuplicateSidebar = useAppStore((state) => state.showDuplicateSidebar)
  const { organizations, isAdmin, switchTo, openCreateOrg, createOrgModal } = useOrgSwitch()

  // All conversation data from sub-nav.tsx
  const { data: activeConversations } = useActiveConversationsQuery(
    (c) => c?.filter((conv) => !conv.agent_secondary_conversation_state)?.length,
  )
  const { data: invitedContacts } = useInvitedContactsQuery((c) => c?.length)
  const authDetails = getLocalAuth()
  const { data: tasksData } = useAllTasksQuery(TaskViewName.AllTasks)
  const { data: myTasksData } = useAllTasksQuery(TaskViewName.MyTasks)
  const { data: unassignedTasks } = useUnassignedTasksQuery()
  const { data: exceptionsTasks } = useAllTasksQuery(TaskViewName.Exceptions)
  const { mutate: favouriteView } = useFavoriteViewMutation()
  const { hasPermission } = usePermissions()
  const setShowDuplicateSidebar = useAppStore((state) => state.setShowDuplicateSidebar)

  const humanSpecialistEnabled = profileData?.organizations?.[0]?.human_specialist_enabled ?? false

  const avatarProps = {
    data: {
      name: `${profileData?.account_first_name} ${profileData?.account_last_name}`,
      email: profileData?.email,
    },
    organizations,
    logoutAction: window.cx.logout,
    statusColor: humanSpecialistEnabled ? agentStatusData?.color : undefined,
    statusMenu: humanSpecialistEnabled ? <StatusMenu /> : undefined,
    orgUuid: profileData?.organizations?.[0]?.org_id,
  }

  const conversationData = {
    favourites,
    publicViews,
    hasViews: Boolean(views),
    // Conversation counts and data
    activeConversations: activeConversations ?? 0,
    invitedContacts: invitedContacts ?? 0,
    tasksData: tasksData?.total_count ?? 0,
    myTasksData: myTasksData?.total_count ?? 0,
    unassignedTasks: unassignedTasks?.total_count ?? 0,
    exceptionsTasks: exceptionsTasks?.total_count ?? 0,
    // User permissions
    isHumanAgent: profileData?.organizations[0]?.is_human_agent,
    hasLiveConversationPermission: hasPermission(
      RbacModules.liveConversation,
      RbacPermissionTypes.view,
    ),
    // Functions
    favouriteView,
    authDetails,
  }

  const handleLogoClick = useCallback(() => {
    navigate(getDefaultRoute())
  }, [navigate])

  const reportSections = useReportSections()
  const workspaceSidebarEnabled = useWorkspaceSidebarEnabled()

  return (
    <>
      <AppSidebar
        avatarProps={profileData ? avatarProps : undefined}
        viewsData={conversationData}
        routes={routes} // Pass filtered routes to sidebar
        handleSwitchOrganization={switchTo}
        showDuplicateSidebar={showDuplicateSidebar}
        setShowDuplicateSidebar={setShowDuplicateSidebar}
        isAdmin={isAdmin}
        onCreateOrganization={isAdmin ? openCreateOrg : undefined}
        onLogoClick={handleLogoClick}
        reportSections={reportSections}
        renderMainSidebar={
          workspaceSidebarEnabled
            ? (state) => (
                <WorkspaceSidebar {...state} routes={routes} reportSections={reportSections} />
              )
            : undefined
        }
        {...props}
      />
      {createOrgModal}
    </>
  )
}
